// @flow
import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import moment from 'moment'
import { containerIds, setUIState } from 'redux/modules/ui'
import { setSearchCondition } from 'redux/modules/search'
import SearchForm from 'components/organisms/SearchForm'
import SearchHelper, { convertToSearchConditionType } from 'helpers/search'
import { loadGoogleMapsApi } from 'frontend_core/dist/helpers/places'
import Path from 'routes/path'

const eventTypeStayId = 'stay'

export type PropTypes = {
  reserveOptions: Array<{
    id: string,
    label: string,
    beforeIcon: string,
    afterIcon: string,
    hoverMessage: string,
    checked: boolean,
  }>,
  amenities: Array<{
    id: string,
    label: string,
    checked: boolean,
  }>,
  eventTypes: Array<{
    id: string,
    label: string,
    checked: boolean,
  }>,
  capacities: Array<{
    label: string,
    checked: boolean,
  }>,
  autoCompleteItems: Array<{
    name: string,
    detail: string,
    original?: ?Object,
  }>,
  sponsoredPromotions: Array<{
    id: number,
    name: string,
  }>,
  showAutoCompleteItems: boolean,
  showCheckInCalendar: boolean,
  showAmenityMore: boolean,
  dayPickerProps: DayPickerRangeController.props,
  singleDayPickerProps: SingleDayPickerRangeController.props,
  budgetRangeProps: Range.props,
  isBudgetUnspecified?: boolean,
  events: {
    areaInput: (text: string) => void,
    areaSelected: (item: ?any) => void,
    areaClose: (e: Event) => void,
    checkInClicked: () => void,
    checkInReset: () => void,
    checkInApply: () => void,
    checkInHourlyApply: () => void,
    checkInClose: (e: Event) => void,
    capacityClicked: () => void,
    capacityInput: (text: string) => void,
    reserveOptionValueChanged: (item: ?any, value: boolean) => void,
    minBudgetInput: (text: string) => void,
    maxBudgetInput: (text: string) => void,
    budgetApply: () => void,
    amenityValueChanged: (item: ?any, value: boolean) => void,
    showMoreAmenity: (expanded: boolean) => void,
    cancelButtonClicked: () => void,
    searchButtonClicked: () => void,
    keywordInput: (text: string) => void,
  },
  fieldValues: {
    area: ?string,
    checkInOut: ?string,
    capacity: ?string,
    editingCapacity: ?number,
    minBudget: ?number,
    maxBudget: ?number,
    keyword: ?string,
    priceType: ?string,
  },
  areaInputRef?: Function,
  momentLocale: string,
}

class Container extends React.Component<PropTypes, *> {
  constructor(props: PropTypes) {
    super(props)

    const searchCondition = this.props.search

    // 初めてきた時はクエリからUIステートをセットする
    let uiState = {}
    uiState.focusedInput = SearchHelper.focusedInputStartDate
    uiState.minPrice = searchCondition.minPrice
    uiState.maxPrice = searchCondition.maxPrice
    uiState.isBudgetUnspecified = !uiState.minPrice && !uiState.maxPrice
    uiState.searchCondition = Object.assign({}, searchCondition)

    if (searchCondition.startedAt !== '' && searchCondition.endedAt !== '') {
      uiState = Object.assign(uiState, {
        startedAt: moment(searchCondition.startedAt),
        endedAt: moment(searchCondition.endedAt),
      })
    }

    if (searchCondition.minCapacity) {
      uiState = Object.assign(uiState, {
        minCapacity: searchCondition.minCapacity,
      })
    }

    this.props.actions.setUIState(containerIds.SEARCH, uiState)
  }

  componentDidMount() {
    // GoogleMapAPIのライブラリを読み込み
    loadGoogleMapsApi(this.props.config.SPM_GOOGLE_API_KEY, 'places,geometry')
  }

  autocompleteService: any
  areaInput: HTMLInputElement

  render() {
    const {
      ui,
      amenities,
      eventTypes,
      capacities,
      actions,
      sponsoredPromotions,
    } = this.props
    const uiSearchCondition = ui.searchCondition || this.props.search

    // 検索条件周り
    const eventTypesProps = eventTypes.map(eventType => ({
      id: eventType.name,
      label: eventType.nameText,
      selected: uiSearchCondition.eventTypes
        ? uiSearchCondition.eventTypes.includes(eventType.name)
        : undefined,
    }))
    // イベントタイプに民泊を追加
    eventTypesProps.push({
      id: eventTypeStayId,
      label: i18n.t('eventType.stay'),
    })

    const checkInValue = SearchHelper.getCheckInValue(
      uiSearchCondition.startedAt,
      uiSearchCondition.endedAt,
      i18n,
    )

    const budgetEvents = {
      minBudgetInput: (text: string) => {
        let minPrice = parseInt(text, 10)
        if (Number.isNaN(minPrice)) {
          minPrice = null
        }

        this.props.actions.setUIState(containerIds.SEARCH, {
          searchCondition: Object.assign({}, uiSearchCondition, {
            minPrice,
            page: 1,
          }),
          isBudgetUnspecified: !minPrice && !uiSearchCondition.maxPrice,
        })
      },
      maxBudgetInput: (text: string) => {
        let maxPrice = parseInt(text, 10)
        if (Number.isNaN(maxPrice)) {
          maxPrice = null
        }
        this.props.actions.setUIState(containerIds.SEARCH, {
          searchCondition: Object.assign({}, uiSearchCondition, {
            maxPrice,
            page: 1,
          }),
          isBudgetUnspecified: !maxPrice && !uiSearchCondition.minPrice,
        })
      },
      budgetApply: () => {},
    }
    // スライダーからの入力では0と101000を未入力として扱う
    const budgetRangeProps = {
      min: 0,
      max: 101000,
      step: 1000,
      pushable: true,
      allowCross: false,
      value: [
        uiSearchCondition.minPrice || 0,
        uiSearchCondition.maxPrice || 101000,
      ],
      onChange: (values: Array<number>) => {
        const minPrice = values[0] >= 1000 ? values[0] : null
        const maxPrice = values[1] <= 100000 ? values[1] : null
        this.props.actions.setUIState(containerIds.SEARCH, {
          searchCondition: Object.assign({}, uiSearchCondition, {
            minPrice,
            maxPrice,
            page: 1,
          }),
          isBudgetUnspecified: !minPrice && !maxPrice,
        })
      },
      onAfterChange: () => {},
    }

    const searchBoxProps = {
      reserveOptions: SearchHelper.getReservedOptions(uiSearchCondition, i18n),
      amenities: amenities.map(amenity => ({
        id: amenity.name,
        label: amenity.nameText,
        checked: uiSearchCondition.amenities
          ? uiSearchCondition.amenities.includes(amenity.name)
          : undefined,
      })),
      eventTypes: eventTypesProps,
      capacities: capacities.map(capacity => ({
        id: capacity.number,
        label: capacity.number,
      })),
      sponsoredPromotions: sponsoredPromotions.map(promo => ({
        id: promo.id,
        label: promo.name,
        checked: uiSearchCondition.sponsoredPromotionId
          ? uiSearchCondition.sponsoredPromotionId.includes(promo.id)
          : undefined,
      })),
      autoCompleteItems: (ui.autoCompleteItems || []).map(item => ({
        name: item.terms[0].value,
        detail: item.description,
        original: item,
      })),
      showAutoCompleteItems: ui.isOpenedAutocomplete,
      showCheckInCalendar: ui.showCheckInCalendar,
      showCapacity: ui.showCapacity,
      showAmenityMore: ui.showAmenityMore,
      dayPickerProps: SearchHelper.defaultDayPickerProps(
        ui.startedAt,
        ui.endedAt,
        ui.focusedInput,
        this.props.actions.setUIState,
        containerIds.SEARCH,
        i18n,
      ),
      singleDayPickerProps: SearchHelper.defaultSingleDayPickerProps(
        ui.startedAt,
        ui.focusedInput,
        this.props.actions.setUIState,
        containerIds.SEARCH,
        i18n,
      ),
      budgetRangeProps,
      isBudgetUnspecified: ui.isBudgetUnspecified,
      events: {
        eventSelected: (item: any) => {
          // 民泊が選択されたらstayの検索に飛ばす
          if (item === eventTypeStayId) {
            window.location.href = Path.staySearch()
          }

          SearchHelper.eventSelected(
            item,
            this.props.actions.setUIState,
            containerIds.SEARCH,
            uiSearchCondition,
          )
        },
        priceTypeSelected: (item: any) =>
          SearchHelper.priceTypeSelected(
            item,
            this.props.actions.setUIState,
            containerIds.SEARCH,
            uiSearchCondition,
          ),
        sartedHourSelected: (item: any) =>
          SearchHelper.sartedHourSelected(
            item,
            this.props.actions.setUIState,
            containerIds.SEARCH,
            uiSearchCondition,
          ),
        endedHourSelected: (item: any) =>
          SearchHelper.endedHourSelected(
            item,
            this.props.actions.setUIState,
            containerIds.SEARCH,
            uiSearchCondition,
          ),
        minCapacitySelected: (item: any) =>
          SearchHelper.minCapacitySelected(
            item,
            this.props.actions.setUIState,
            containerIds.SEARCH,
            uiSearchCondition,
          ),
        maxCapacitySelected: (item: any) =>
          SearchHelper.maxCapacitySelected(
            item,
            this.props.actions.setUIState,
            containerIds.SEARCH,
            uiSearchCondition,
          ),
        areaInput: async (text: string) => {
          if (
            !this.autocompleteService &&
            google &&
            google.maps &&
            google.maps.places
          ) {
            this.autocompleteService = new google.maps.places.AutocompleteService()
          }
          SearchHelper.areaInput(
            text,
            this.props.actions.setUIState,
            containerIds.SEARCH,
            uiSearchCondition,
            this.autocompleteService,
            i18n,
          )
        },
        areaSelected: async (item: any) =>
          SearchHelper.areaSelected(
            item,
            this.props.actions.setUIState,
            containerIds.SEARCH,
            uiSearchCondition,
            this.areaInput,
          ),
        areaClose: () =>
          SearchHelper.areaClose(
            this.props.actions.setUIState,
            containerIds.SEARCH,
          ),
        checkInClicked: () =>
          SearchHelper.checkInClicked(
            ui.showCheckInCalendar,
            this.props.actions.setUIState,
            containerIds.SEARCH,
          ),
        checkInReset: () =>
          SearchHelper.checkInReset(
            this.props.actions.setUIState,
            containerIds.SEARCH,
            uiSearchCondition,
          ),
        checkInApply: () =>
          SearchHelper.checkInApply(
            ui.startedAt,
            ui.endedAt,
            this.props.actions.setUIState,
            containerIds.SEARCH,
            uiSearchCondition,
          ),
        checkInHourlyApply: () =>
          SearchHelper.checkInHourlyApply(
            ui.startedAt,
            ui.endedAt,
            this.props.actions.setUIState,
            containerIds.SEARCH,
            uiSearchCondition,
          ),
        checkInClose: () =>
          SearchHelper.checkInClose(
            this.props.actions.setUIState,
            containerIds.SEARCH,
          ),
        capacityClicked: () =>
          SearchHelper.capacityClicked(
            ui.showCapacity,
            this.props.actions.setUIState,
            containerIds.SEARCH,
            uiSearchCondition,
          ),
        capacityAdd: (addNumber: number) =>
          SearchHelper.capacityAdd(
            addNumber,
            this.props.actions.setUIState,
            containerIds.SEARCH,
            ui.minCapacity,
          ),
        capacityInput: (text: string) =>
          SearchHelper.capacityInput(
            text,
            this.props.actions.setUIState,
            containerIds.SEARCH,
          ),
        capacityReset: () =>
          SearchHelper.capacityReset(
            this.props.actions.setUIState,
            containerIds.SEARCH,
            uiSearchCondition,
          ),
        capacityApply: () =>
          SearchHelper.capacityApply(
            this.props.actions.setUIState,
            containerIds.SEARCH,
            ui.minCapacity,
            uiSearchCondition,
          ),
        capacityClose: () =>
          SearchHelper.capacityClose(
            this.props.actions.setUIState,
            containerIds.SEARCH,
          ),
        reserveOptionValueChanged: (item: any, value: boolean) =>
          SearchHelper.reserveOptionValueChanged(
            item,
            value,
            this.props.actions.setUIState,
            containerIds.SEARCH,
            uiSearchCondition,
          ),
        minBudgetInput: budgetEvents.minBudgetInput,
        maxBudgetInput: budgetEvents.maxBudgetInput,
        budgetApply: budgetEvents.budgetApply,
        amenityValueChanged: (item: ?any, value: boolean) => {
          if (item) {
            let newAmenities = (uiSearchCondition.amenities || []).concat()
            if (value) {
              newAmenities.push(item.id)
            } else {
              newAmenities = newAmenities.filter(type => item.id !== type)
            }
            this.props.actions.setUIState(containerIds.SEARCH, {
              searchCondition: Object.assign({}, uiSearchCondition, {
                amenities: newAmenities,
                page: 1,
              }),
            })
          }
        },
        sponsoredPromotionsValueChanged: (item: ?any, value: boolean) => {
          if (item) {
            let newSponsoredPromotionId = (
              uiSearchCondition.sponsoredPromotionId || []
            ).concat()
            if (value) {
              newSponsoredPromotionId.push(item.id)
            } else {
              newSponsoredPromotionId = newSponsoredPromotionId.filter(
                type => item.id !== type,
              )
            }
            this.props.actions.setUIState(containerIds.SEARCH, {
              searchCondition: Object.assign({}, uiSearchCondition, {
                sponsoredPromotionId: newSponsoredPromotionId,
                page: 1,
              }),
            })
          }
        },
        showMoreAmenity: (expanded: boolean) => {
          this.props.actions.setUIState(containerIds.SEARCH, {
            showAmenityMore: expanded,
          })
        },
        cancelButtonClicked: () => {
          this.props.actions.setUIState(containerIds.SEARCH, {
            showSearchForm: false,
            isShowHeaderSearch: false,
          })
        },
        searchButtonClicked: e => {
          this.props.actions.setSearchCondition(
            Object.assign(uiSearchCondition, { isNewQuery: true }),
          )
          this.props.onSubmit(e)
        },
        keywordInput: (text: string) => {
          this.props.actions.setUIState(containerIds.SEARCH, {
            searchCondition: Object.assign({}, uiSearchCondition, {
              keyword: text,
              page: 1,
            }),
          })
        },
      },
      fieldValues: {
        area: ui.locationText || uiSearchCondition.locationText || '',
        eventType: uiSearchCondition.eventType || '',
        checkInOut: checkInValue,
        minCapacity: uiSearchCondition.minCapacity || '',
        maxCapacity: uiSearchCondition.maxCapacity || '',
        editingCapacity: ui.minCapacity || '',
        minBudget: uiSearchCondition.minPrice || '',
        maxBudget: uiSearchCondition.maxPrice || '',
        keyword: uiSearchCondition.keyword || '',
        priceType: uiSearchCondition.priceType || '',
        sartedHour: uiSearchCondition.sartedHour || '',
        endedHour: uiSearchCondition.endedHour || '',
      },
      areaInputRef: areaInput => {
        this.areaInput = areaInput
      },
      momentLocale: moment.locale(),
    }

    return (
      <SearchForm
        {...budgetRangeProps}
        {...searchBoxProps}
        searchSessionId={this.props.searchSessionId}
      />
    )
  }
}

function mapStateToProps(state: Object) {
  return state
}
function mapDispatchToProps(dispatch: Dispatch<*>) {
  const bindActions = { setUIState, setSearchCondition }
  return { actions: bindActionCreators(bindActions, dispatch) }
}
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Container)



// WEBPACK FOOTER //
// ./src/containers/organisms/SearchForm.jsx